import cloneDeep from "lodash.clonedeep"
import commonUtil from "@/utils/commonUtil";

const labelPrefix = "highlight-";
const shortString = {
    none: "",
    owneddomain: "own-domain",
    ownedpage: "own-page",
    notowned: 'own-not-owned',
    resembler: "own-resembler",
    "-1": "rt-neg",
    "0": "rt-soso",
    "1": "rt-pos",
    "-4": "",
    unmarked: "",
    social: "ath-social",
    crowd: "ath-crowd",
    expert: "ath-expert",
    iauthority: "ath-iauth",
    wikipedia: "ath-wiki",
}

const shortStringAI = {
    "-1": "rt-neg-ai",
    "0": "rt-soso-ai",
    "1": "rt-pos-ai",
}

function markupToClasses(element) {
    const markup = element.markup || []
    const aiRating = (markup.rating === -4) ? (shortStringAI[element.aimarkup?.rating] || "") : "";
    return [Object.values(markup).map(value => shortString[value]), aiRating].flat().join(" ");
}

function markupSelectionsToLabels(classes) {
    return classes
        .flatMap((cl) => cl === "own-owned" ? ["own-domain", "own-page"] : cl)
        .map((cl) => labelPrefix + cl);
}

function updateMarkupForResult(result, newMarkup, datetime) {
    result.markup = cloneDeep(newMarkup);

    if (result.markup.ratingHistory) {
        const cardTime = Date.parse(datetime) / 1000;
        const relevantRatingKey = Object.keys(result.markup.ratingHistory).filter(ts => +ts <= cardTime).sort().reverse()[0];
        result.markup.rating = result.markup.ratingHistory[relevantRatingKey];
    }
}

// DM-10271: a People-Also-Ask answer lists its sources in `urls`, each with its own markup.
const withSources = (sections) => sections.flatMap((o) => [o, ...(o.urls || [])]);
// DM-11385: so does an AI Overview inside a Knowledge Panel.
const kpAioSources = (result) => result.kp?.aioverview?.urls || [];

function updateTimelineMarkup(card, markupByUrlId) {
    const needsUpdate = (o) => Object.keys(markupByUrlId).includes(o.urlid + '');

    card.result.forEach((result, index) => {
        if (result.computed.containerDetailSections) {
            withSources(result.computed.containerDetailSections).filter(needsUpdate).forEach((o) => updateMarkupForResult(o, markupByUrlId[o.urlid], card.datetime));
        } else if (result.computed.detailSection && needsUpdate(result.computed.detailSection)) {
            updateMarkupForResult(result.computed.detailSection, markupByUrlId[result.computed.detailSection.urlid], card.datetime);
            if (result.computed.hasSiteLinks) {
                card.result[index + 1].sitelink.forEach(sitelink => updateMarkupForResult(sitelink, markupByUrlId[result.computed.detailSection.urlid], card.datetime));
            }
        }
        kpAioSources(result).filter(needsUpdate).forEach((o) => updateMarkupForResult(o, markupByUrlId[o.urlid], card.datetime));
    });

}

function getPossiblyAffectedUrlIds(cards, markupEditorData) {
    const domain = commonUtil.extractDomain(markupEditorData.url);
    const urlids = new Set();
    urlids.add(markupEditorData.urlid);

    cards.forEach(card => {
        card.result.forEach(result => {
            if (result.computed.containerDetailSections) {
                withSources(result.computed.containerDetailSections).forEach((o) => addIfPossiblyAffected(o, o.resulttype));
            } else if (result.computed.detailSection) {
                addIfPossiblyAffected(result.computed.detailSection, result.resulttype)
            }
            kpAioSources(result).forEach((o) => addIfPossiblyAffected(o, 'knowledgegraph'));
        });
    });

    function addIfPossiblyAffected(o, resultType) {
        // a section without a urlid would go out as null, and the request is rejected (DM-11421)
        if (o.urlid && isPossiblyAffected(o, resultType)) {
            urlids.add(o.urlid);
        }
    }

    /**
     * Adapted from legacy implementation.
     * There it handles youtube carousels but that's not needed so it was left out here.
     * I'm not sure why we don't look for same domain URLs in the listed container types.
     * 
     * @param { object } otherResult 
     * @param { string } changedResultType 
     * @returns 
     */
    function isPossiblyAffected(otherResult, changedResultType) {
        const ignoreTypes = ['videocontainervideo', 'newscontainernews', 'articlecontainernews'];
        if (!ignoreTypes.includes(changedResultType) && commonUtil.extractDomain(otherResult.url) === domain) {
            return true;
        }

        return false;
    }

    return Array.from(urlids);
}

export default {
    markupToClasses,
    markupSelectionsToLabels,
    getPossiblyAffectedUrlIds,
    updateTimelineMarkup,
};